// ---------------------------------------------------------------------------
// ICD-10 CMS fixed-width format column positions
// ---------------------------------------------------------------------------

/** Minimum line length for a valid ICD-10 data row */
export const ICD10_MIN_LINE_LENGTH = 16;
/** Start column for the ICD-10 code (0-indexed) */
export const ICD10_CODE_COL_START = 6;
/** End column (exclusive) for the ICD-10 code */
export const ICD10_CODE_COL_END = 13;
/** Column index of the billable flag (0 or 1) */
export const ICD10_BILLABLE_FLAG_COL = 14;
/** Start column for the short description */
export const ICD10_SHORT_DESC_COL_START = 16;
/** End column (exclusive) for the short description */
export const ICD10_SHORT_DESC_COL_END = 76;
/** Start column for the long description */
export const ICD10_LONG_DESC_COL_START = 77;
/** Length of the ICD-10 category prefix (e.g. "F15") */
export const ICD10_CATEGORY_LENGTH = 3;
/** Minimum raw code length that requires a dot insertion */
export const ICD10_DOT_INSERT_MIN_LENGTH = 4;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ParsedCode = {
  code: string;
  description: string;
  longDescription: string;
};

export type DeletedCode = { year: string; descriptor: string };

// ---------------------------------------------------------------------------
// Parsers
// ---------------------------------------------------------------------------

export const getCptCategory = (code: string): string => {
  if (/^\d{5}$/.test(code)) return 'I';
  if (/^\d{4}F$/.test(code)) return 'II';
  if (/^\d{4}T$/.test(code)) return 'III';
  if (/^\d{4}M$/.test(code)) return 'M';
  if (/^\d{4}U$/.test(code)) return 'PLA';
  return '';
};

/**
 * Parses a single line of the CMS ICD-10 order file using fixed column positions.
 * Format: cols 1-5 order#, 7-13 code (7 chars), 15 billable flag, 17-76 short desc (60 chars), 78+ long desc.
 * Returns null if the line is too short to be a data row.
 */
export const parseIcd10Line = (
  line: string,
): {
  raw: string;
  billable: boolean;
  shortDesc: string;
  longDesc: string;
} | null => {
  if (line.length < ICD10_MIN_LINE_LENGTH) return null;
  const raw = line.substring(ICD10_CODE_COL_START, ICD10_CODE_COL_END).trim();
  if (!raw) return null;
  const billable = line[ICD10_BILLABLE_FLAG_COL] === '1';
  const shortDesc = line
    .substring(ICD10_SHORT_DESC_COL_START, ICD10_SHORT_DESC_COL_END)
    .trim();
  const longDesc =
    line.length > ICD10_LONG_DESC_COL_START
      ? line.substring(ICD10_LONG_DESC_COL_START).trim()
      : shortDesc;
  return { raw, billable, shortDesc, longDesc };
};

export const icd10DotCode = (raw: string) =>
  raw.length >= ICD10_DOT_INSERT_MIN_LENGTH
    ? `${raw.substring(0, ICD10_CATEGORY_LENGTH)}.${raw.substring(ICD10_CATEGORY_LENGTH)}`
    : raw;

/**
 * Returns the set of ALL code strings in the CMS ICD-10 order file (billable and non-billable).
 * Used to distinguish "genuinely removed by CMS" from "non-billable codes that shouldn't have been stored".
 */
export const parseIcd10AllCodes = (text: string): Set<string> => {
  const all = new Set<string>();
  for (const line of text.split(/\r?\n/)) {
    const parsed = parseIcd10Line(line);
    if (!parsed) continue;
    all.add(icd10DotCode(parsed.raw));
  }
  return all;
};

/**
 * Parses icd10cm_order_YYYY.txt (CMS fixed-width format).
 * Only rows with billable=1 are leaf codes.
 * Returns null if the file does not look like a CMS ICD-10 order file.
 */
export const parseIcd10 = (text: string): ParsedCode[] | null => {
  const lines = text.split(/\r?\n/);
  // Validate file identity: first non-empty line must match fixed-width CMS format
  // (5-digit order#, space, letter-starting code at col 6, flag 0/1 at col 14)
  const firstLine = lines.find((l) => l.trim().length > 0) ?? '';
  if (
    !/^\d{5} [A-Z]/.test(firstLine) ||
    (firstLine[ICD10_BILLABLE_FLAG_COL] !== '0' &&
      firstLine[ICD10_BILLABLE_FLAG_COL] !== '1')
  ) {
    return null;
  }
  const codes: ParsedCode[] = [];
  for (const line of lines) {
    const parsed = parseIcd10Line(line);
    if (!parsed || !parsed.billable) continue;
    codes.push({
      code: icd10DotCode(parsed.raw),
      description: parsed.shortDesc,
      longDescription: parsed.longDesc,
    });
  }
  return codes;
};

/**
 * Parses the AMA "Consolidated Code List.txt" (tab-delimited).
 * Data begins at the line starting with "Concept Id\t".
 */
export const parseCpt = (text: string): ParsedCode[] | null => {
  const codes: ParsedCode[] = [];
  const lines = text.split(/\r?\n/);

  const headerIdx = lines.findIndex((l) => l.startsWith('Concept Id\t'));
  if (headerIdx === -1) return null;

  const headers = (lines[headerIdx] ?? '').split('\t');
  const cptIdx = headers.indexOf('CPT Code');
  const longIdx = headers.indexOf('Long');

  if (cptIdx === -1) return null;

  for (let i = headerIdx + 1; i < lines.length; i++) {
    const line = lines[i] ?? '';
    if (!line.trim()) continue;
    const cols = line.split('\t');
    const code = cols[cptIdx]?.trim();
    if (!code) continue;

    codes.push({
      code,
      description: (longIdx !== -1 ? cols[longIdx]?.trim() : '') ?? '',
      longDescription: (longIdx !== -1 ? cols[longIdx]?.trim() : '') ?? '',
    });
  }

  return codes;
};

/**
 * Parses the CMS HCPCS Level II ANWEB fixed-width file (HCPC2026_APR_ANWEB.txt).
 * Primary records start with an alpha char at position 0 and have seq "001" at positions 5-8.
 * Code: chars 0-4, description: chars 11-91.
 */
export const parseHcpcs = (text: string): ParsedCode[] | null => {
  const codes: ParsedCode[] = [];

  for (const line of text.split(/\r?\n/)) {
    if (!line || !/^[A-Za-z]/.test(line)) continue;
    if (line.length < 91) continue;
    if (line.substring(5, 8) !== '001') continue;
    const code = line.substring(0, 5).trim();
    const description = line.substring(11, 91).trim();
    if (!code || !description) continue;
    codes.push({ code, description, longDescription: description });
  }

  // If nothing matched the HCPCS primary record pattern, file is wrong format
  if (codes.length === 0) return null;
  return codes;
};

/**
 * Parses the Australian MBS Online Description file (tab-delimited).
 * Only rows with LATEST='Y' are current items.
 * Format: ITEM\tDESCRIPTION_START\tDESCRIPTION_END\tLATEST\tDescription
 */
export const parseMbs = (text: string): ParsedCode[] | null => {
  const codes: ParsedCode[] = [];
  const lines = text.split(/\r?\n/);

  // Find the header line (tolerates a UTF-8 BOM, leading blank lines, or a
  // short preamble) rather than requiring it on the first physical line.
  const headerIdx = lines.findIndex(
    (l) => l.includes('ITEM') && l.includes('LATEST'),
  );
  if (headerIdx === -1) return null;

  for (let i = headerIdx + 1; i < lines.length; i++) {
    const line = lines[i] ?? '';
    if (!line.trim()) continue;
    const cols = line.split('\t');
    if (cols.length < 5) continue;
    // Normalize LATEST so a stray space/case (e.g. 'Y ', 'y') doesn't drop a
    // current item — government exports aren't guaranteed clean.
    if (cols[3]?.trim().toUpperCase() !== 'Y') continue;

    const rawCode = cols[0]?.trim();
    if (!rawCode) continue;
    const codeNumber = Number(rawCode);
    // Skip non-numeric ITEM values instead of storing "NaN" as a code
    if (!Number.isFinite(codeNumber)) continue;
    const code = String(codeNumber); // trim leading zeros: 00023 → 23
    // Description is the last column; rejoin everything from col 4 so an
    // embedded tab in the description isn't truncated.
    const description = cols.slice(4).join('\t').trim();
    if (!description) continue;

    codes.push({
      code,
      // MBS has no short descriptor; store full text so UI and search see it all
      description,
      longDescription: description,
    });
  }

  if (codes.length === 0) return null;
  return codes;
};

/**
 * Parses the AMA "Historically Deleted Codes.txt" (tab-delimited).
 * Returns a Map of code -> { year, descriptor }.
 */
export const parseHistoricallyDeleted = (
  text: string,
): Map<string, DeletedCode> | null => {
  const map = new Map<string, DeletedCode>();
  const lines = text.split(/\r?\n/);
  // Search for header line (tolerates BOM, blank lines, preamble)
  const headerIdx = lines.findIndex((l) => {
    const lower = l.toLowerCase();
    return lower.includes('cpt code') && lower.includes('year');
  });
  if (headerIdx === -1) return null;
  for (const line of lines.slice(headerIdx + 1)) {
    const cols = line.split('\t');
    if (cols.length < 2) continue;
    const code = cols[0]?.trim();
    const year = cols[1]?.trim();
    const descriptor = cols[2]?.trim() ?? '';
    if (code && year && /^\d{4}$/.test(year))
      map.set(code, { year, descriptor });
  }
  return map;
};
